import React, { useState, useEffect, createContext, useContext, useRef } from 'react';

// Context for App-wide state
const AppContext = createContext(null);

const App = () => {
    const [currentPage, setCurrentPage] = useState('products'); // 'products', 'costing', 'dishes', 'inventory'
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [editingDish, setEditingDish] = useState(null); // New state to hold the dish being edited

    useEffect(() => {
        setLoading(false); // App is ready immediately
    }, []);

    // Function to navigate to costing page and set a dish for editing
    const startEditingDish = (dish) => {
        setEditingDish(dish);
        setCurrentPage('costing');
    };

    // Function to clear editing state
    const clearEditingDish = () => {
        setEditingDish(null);
    };

    if (loading) {
        return (
            <div className="flex items-center justify-center min-h-screen bg-gray-100">
                <div className="text-xl font-semibold text-gray-700">Cargando aplicación...</div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="flex items-center justify-center min-h-screen bg-red-100 text-red-700 p-4 rounded-lg">
                <div className="text-xl font-semibold">{error}</div>
            </div>
        );
    }

    return (
        <AppContext.Provider value={{ startEditingDish, clearEditingDish }}>
            <div className="min-h-screen bg-gray-100 font-inter text-gray-800 flex flex-col">
                <header className="bg-gradient-to-r from-purple-700 to-indigo-800 text-white p-4 shadow-lg">
                    <div className="container mx-auto flex flex-col sm:flex-row justify-between items-center">
                        <h1 className="text-3xl font-bold mb-2 sm:mb-0">Costeo de Platillos</h1>
                        <nav className="flex flex-wrap justify-center sm:justify-end gap-3">
                            <button
                                onClick={() => { setCurrentPage('products'); clearEditingDish(); }}
                                className={`px-4 py-2 rounded-lg transition-all duration-300 ${
                                    currentPage === 'products' ? 'bg-white text-purple-700 shadow-md' : 'text-white hover:bg-purple-700/80'
                                }`}
                            >
                                Productos
                            </button>
                            <button
                                onClick={() => { setCurrentPage('costing'); clearEditingDish(); }}
                                className={`px-4 py-2 rounded-lg transition-all duration-300 ${
                                    currentPage === 'costing' ? 'bg-white text-purple-700 shadow-md' : 'text-white hover:bg-purple-700/80'
                                }`}
                            >
                                Costeo de Platillos
                            </button>
                            <button
                                onClick={() => { setCurrentPage('dishes'); clearEditingDish(); }}
                                className={`px-4 py-2 rounded-lg transition-all duration-300 ${
                                    currentPage === 'dishes' ? 'bg-white text-purple-700 shadow-md' : 'text-white hover:bg-purple-700/80'
                                }`}
                            >
                                Platillos Guardados
                            </button>
                            <button
                                onClick={() => { setCurrentPage('inventory'); clearEditingDish(); }}
                                className={`px-4 py-2 rounded-lg transition-all duration-300 ${
                                    currentPage === 'inventory' ? 'bg-white text-purple-700 shadow-md' : 'text-white hover:bg-purple-700/80'
                                }`}
                            >
                                Inventario
                            </button>
                        </nav>
                    </div>
                </header>

                <main className="flex-grow container mx-auto p-4 sm:p-6">
                    {(() => {
                        switch (currentPage) {
                            case 'products':
                                return <ProductManagement />;
                            case 'costing':
                                return <DishCosting dishToEdit={editingDish} />;
                            case 'dishes':
                                return <DishList />;
                            case 'inventory':
                                return <InventoryManagement />;
                            default:
                                return <ProductManagement />;
                        }
                    })()}
                </main>

                <footer className="bg-gray-900 text-white p-4 text-center text-sm shadow-inner mt-auto">
                    <p>Aplicación de Costeo de Platillos - Almacenamiento Local</p>
                    <p>Los datos se guardan en tu navegador y no se sincronizan entre dispositivos.</p>
                </footer>
            </div>
        </AppContext.Provider>
    );
};

// Helper to generate unique IDs for local storage items
const generateUniqueId = () => '_' + Math.random().toString(36).substr(2, 9);

// Helper to escape CSV values
const escapeCsvValue = (value) => {
    if (value === null || value === undefined) {
        return '';
    }
    let stringValue = String(value);
    // If the value contains a comma, double quote, or newline, enclose it in double quotes
    if (stringValue.includes(',') || stringValue.includes('"') || stringValue.includes('\n')) {
        // Escape double quotes by doubling them
        stringValue = stringValue.replace(/"/g, '""');
        return `"${stringValue}"`;
    }
    return stringValue;
};


const ProductManagement = () => {
    const [productName, setProductName] = useState('');
    const [unit, setUnit] = useState('');
    const [quantity, setQuantity] = useState('');
    const [cost, setCost] = useState('');
    const [products, setProducts] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [isEditing, setIsEditing] = useState(false);
    const [currentProduct, setCurrentProduct] = useState(null);

    // Lucide React icons
    const PlusCircle = () => <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="lucide lucide-plus-circle"><circle cx="12" cy="12" r="10"/><path d="M8 12h8"/><path d="M12 8v8"/></svg>;
    const Edit = () => <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="lucide lucide-edit"><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/><path d="M18.5 2.5a2.12 2.12 0 0 1 3 3L12 15l-4 1 1-4Z"/></svg>;
    const Trash2 = () => <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="lucide lucuce-trash-2"><path d="M3 6h18"/><path d="M19 6v14c0 1-1 2-2 2H7c-1 0-2-1-2-2V6"/><path d="M8 6V4c0-1 1-2 2-2h4c1 0 2 1 2 2v2"/><line x1="10" x2="10" y1="11" y2="17"/><line x1="14" x2="14" y1="11" y2="17"/></svg>;
    const Save = () => <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="lucide lucide-save"><path d="M19 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11l5 5v11a2 2 0 0 1-2 2Z"/><polyline points="17 21 17 13 7 13 7 21"/><polyline points="7 3 7 8 15 8"/></svg>;
    const XCircle = () => <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="lucide lucide-x-circle"><circle cx="12" cy="12" r="10"/><path d="m15 9-6 6"/><path d="m9 9 6 6"/></svg>;
    const Upload = () => <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="lucide lucide-upload"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="17 8 12 3 7 8"/><line x1="12" x2="12" y1="3" y2="15"/></svg>;
    const Download = () => <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="lucide lucide-download"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" x2="12" y1="15" y2="3"/></svg>;


    // Load products from localStorage on component mount
    useEffect(() => {
        try {
            const storedProducts = localStorage.getItem('restaurant_products');
            if (storedProducts) {
                setProducts(JSON.parse(storedProducts));
            }
        } catch (e) {
            console.error("Error loading products from localStorage:", e);
            setError("Error al cargar productos desde el almacenamiento local.");
        } finally {
            setLoading(false);
        }
    }, []);

    // Save products to localStorage whenever products state changes
    useEffect(() => {
        if (!loading) { // Only save after initial load to prevent overwriting
            localStorage.setItem('restaurant_products', JSON.stringify(products));
        }
    }, [products, loading]);

    const handleAddProduct = (e) => {
        e.preventDefault();
        if (!productName || !unit || !quantity || !cost) {
            setError("Por favor, complete todos los campos.");
            return;
        }

        const qtyNum = parseFloat(quantity);
        const costNum = parseFloat(cost);

        if (isNaN(qtyNum) || isNaN(costNum) || qtyNum <= 0 || costNum <= 0) {
            setError("Cantidad y costo deben ser números positivos.");
            return;
        }

        const costPerUnit = costNum / qtyNum;

        try {
            if (isEditing) {
                setProducts(prevProducts => prevProducts.map(p =>
                    p.id === currentProduct.id
                        ? { ...p, name: productName, unit: unit, quantity: qtyNum, originalCost: costNum, costPerUnit: costPerUnit }
                        : p
                ));
                setIsEditing(false);
                setCurrentProduct(null);
                console.log("Producto actualizado:", productName);
            } else {
                const newProduct = {
                    id: generateUniqueId(),
                    name: productName,
                    unit: unit,
                    quantity: qtyNum,
                    originalCost: costNum,
                    costPerUnit: costPerUnit,
                };
                setProducts(prevProducts => [...prevProducts, newProduct]);
                console.log("Producto agregado:", productName);
            }
            setProductName('');
            setUnit('');
            setQuantity('');
            setCost('');
            setError(null);
        } catch (e) {
            console.error("Error al agregar/actualizar producto:", e);
            setError("Error al guardar el producto. Intente de nuevo.");
        }
    };

    const handleEditClick = (product) => {
        setIsEditing(true);
        setCurrentProduct(product);
        setProductName(product.name);
        setUnit(product.unit);
        setQuantity(product.quantity);
        setCost(product.originalCost);
        setError(null);
    };

    const handleDeleteProduct = (id) => {
        if (window.confirm("¿Estás seguro de que quieres eliminar este producto?")) {
            try {
                setProducts(prevProducts => prevProducts.filter(p => p.id !== id));
                console.log("Producto eliminado:", id);
            } catch (e) {
                console.error("Error al eliminar producto:", e);
                setError("Error al eliminar el producto. Intente de nuevo.");
            }
        }
    };

    const handleCancelEdit = () => {
        setIsEditing(false);
        setCurrentProduct(null);
        setProductName('');
        setUnit('');
        setQuantity('');
        setCost('');
        setError(null);
    };

    const exportProductsToCsv = () => {
        if (products.length === 0) {
            alert("No hay productos para exportar.");
            return;
        }
        const headers = ["id", "name", "unit", "quantity", "originalCost", "costPerUnit"];
        const csvRows = [];
        csvRows.push(headers.map(escapeCsvValue).join(',')); // Add headers

        products.forEach(product => {
            const row = headers.map(header => escapeCsvValue(product[header]));
            csvRows.push(row.join(','));
        });

        const csvString = csvRows.join('\n');
        const blob = new Blob([csvString], { type: 'text/csv;charset=utf-8;' });
        // Use FileSaver.js saveAs function (loaded via script tag in index.html)
        if (typeof window.saveAs === 'function') {
            window.saveAs(blob, 'productos.csv');
        } else {
            // Fallback for browsers not supporting saveAs or if library not loaded
            const link = document.createElement('a');
            link.href = URL.createObjectURL(blob);
            link.setAttribute('download', 'productos.csv');
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
        }
    };

    const importProductsFromCsv = (event) => {
        const file = event.target.files[0];
        if (!file) {
            return;
        }

        const reader = new FileReader();
        reader.onload = (e) => {
            const text = e.target.result;
            const lines = text.split('\n').filter(line => line.trim() !== ''); // Filter empty lines

            if (lines.length === 0) {
                setError("El archivo CSV está vacío.");
                return;
            }

            // Basic CSV parsing: assuming header row and comma delimiter
            const headers = lines[0].split(',').map(h => h.trim());
            const expectedHeaders = ["id", "name", "unit", "quantity", "originalCost", "costPerUnit"];

            // Simple check for expected headers (order doesn't matter for this check)
            const missingHeaders = expectedHeaders.filter(header => !headers.includes(header));
            if (missingHeaders.length > 0) {
                setError(`Encabezados faltantes en el CSV: ${missingHeaders.join(', ')}. Asegúrate de que el archivo tenga los encabezados: ${expectedHeaders.join(', ')}`);
                return;
            }

            const newProducts = [];
            let importErrors = [];

            for (let i = 1; i < lines.length; i++) {
                const values = lines[i].split(',').map(v => v.trim()); // Trim values
                // Handle quoted values (simple unquoting)
                const unquotedValues = values.map(v => v.startsWith('"') && v.endsWith('"') ? v.slice(1, -1).replace(/""/g, '"') : v);

                if (unquotedValues.length !== headers.length) {
                    importErrors.push(`Fila ${i + 1} tiene un número incorrecto de columnas. Saltando.`);
                    continue;
                }

                const productData = {};
                headers.forEach((header, index) => {
                    productData[header] = unquotedValues[index];
                });

                const id = productData.id || generateUniqueId(); // Use existing ID or generate new
                const name = productData.name;
                const unit = productData.unit;
                const quantity = parseFloat(productData.quantity);
                const originalCost = parseFloat(productData.originalCost);

                if (!name || !unit || isNaN(quantity) || isNaN(originalCost) || quantity <= 0 || originalCost <= 0) {
                    importErrors.push(`Fila ${i + 1} contiene datos inválidos o faltantes. Saltando.`);
                    continue;
                }

                const costPerUnit = originalCost / quantity;

                newProducts.push({
                    id: id,
                    name: name,
                    unit: unit,
                    quantity: quantity,
                    originalCost: originalCost,
                    costPerUnit: costPerUnit,
                });
            }

            if (importErrors.length > 0) {
                setError(`Errores durante la importación:\n${importErrors.join('\n')}`);
            } else {
                setError(null);
            }

            // Merge new products with existing ones, updating if ID matches
            const updatedProductsMap = new Map(products.map(p => [p.id, p]));
            newProducts.forEach(newP => updatedProductsMap.set(newP.id, newP));
            setProducts(Array.from(updatedProductsMap.values()));
            alert("Productos importados exitosamente. Revisa la tabla.");
            // Clear the file input after processing
            event.target.value = '';
        };
        reader.readAsText(file);
    };


    if (loading) {
        return <div className="text-center text-lg mt-8">Cargando productos...</div>;
    }

    if (error) {
        return <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded-lg relative mb-4" role="alert">
            <span className="block sm:inline">{error}</span>
        </div>;
    }

    return (
        <div className="bg-white p-6 rounded-xl shadow-lg">
            <h2 className="text-2xl font-semibold mb-6 text-purple-700">Gestión de Productos</h2>

            <form onSubmit={handleAddProduct} className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mb-8">
                <div className="flex flex-col">
                    <label htmlFor="productName" className="text-sm font-medium text-gray-700 mb-1">Nombre del Producto:</label>
                    <input
                        type="text"
                        id="productName"
                        value={productName}
                        onChange={(e) => setProductName(e.target.value)}
                        className="p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                        placeholder="Ej. Harina de trigo"
                        required
                    />
                </div>
                <div className="flex flex-col">
                    <label htmlFor="unit" className="text-sm font-medium text-gray-700 mb-1">Unidad de Medida:</label>
                    <input
                        type="text"
                        id="unit"
                        value={unit}
                        onChange={(e) => setUnit(e.target.value)}
                        className="p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                        placeholder="Ej. kg, litro, pieza"
                        required
                    />
                </div>
                <div className="flex flex-col">
                    <label htmlFor="quantity" className="text-sm font-medium text-gray-700 mb-1">Cantidad (en unidad):</label>
                    <input
                        type="number"
                        id="quantity"
                        value={quantity}
                        onChange={(e) => setQuantity(e.target.value)}
                        className="p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                        placeholder="Ej. 1"
                        step="0.01"
                        required
                    />
                </div>
                <div className="flex flex-col">
                    <label htmlFor="cost" className="text-sm font-medium text-gray-700 mb-1">Costo Total (por la cantidad):</label>
                    <input
                        type="number"
                        id="cost"
                        value={cost}
                        onChange={(e) => setCost(e.target.value)}
                        className="p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                        placeholder="Ej. 25.50"
                        step="0.01"
                        required
                    />
                </div>
                <div className="md:col-span-2 lg:col-span-3 flex justify-end space-x-2 mt-2">
                    {isEditing ? (
                        <>
                            <button
                                type="submit"
                                className="bg-green-600 hover:bg-green-700 text-white font-bold py-2 px-4 rounded-lg flex items-center transition-colors duration-300 shadow-md"
                            >
                                <Save className="mr-2" /> Guardar Cambios
                            </button>
                            <button
                                type="button"
                                onClick={handleCancelEdit}
                                className="bg-gray-500 hover:bg-gray-600 text-white font-bold py-2 px-4 rounded-lg flex items-center transition-colors duration-300 shadow-md"
                            >
                                <XCircle className="mr-2" /> Cancelar
                            </button>
                        </>
                    ) : (
                        <button
                            type="submit"
                            className="bg-purple-600 hover:bg-purple-700 text-white font-bold py-2 px-4 rounded-lg flex items-center transition-colors duration-300 shadow-md"
                        >
                            <PlusCircle className="mr-2" /> Agregar Producto
                        </button>
                    )}
                </div>
            </form>

            <div className="flex justify-end space-x-3 mb-6">
                <button
                    onClick={exportProductsToCsv}
                    className="bg-gray-700 hover:bg-gray-800 text-white font-bold py-2 px-4 rounded-lg flex items-center transition-colors duration-300 shadow-md"
                >
                    <Download className="mr-2" /> Exportar Productos (.csv)
                </button>
                <label htmlFor="importProductsInput" className="bg-blue-600 hover:bg-blue-700 text-white font-bold py-2 px-4 rounded-lg flex items-center cursor-pointer transition-colors duration-300 shadow-md">
                    <Upload className="mr-2" /> Importar Productos (.csv)
                    <input
                        id="importProductsInput"
                        type="file"
                        accept=".csv"
                        onChange={importProductsFromCsv}
                        className="hidden"
                    />
                </label>
            </div>

            {error && (
                <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded-lg relative mb-4" role="alert">
                    <span className="block sm:inline">{error}</span>
                </div>
            )}

            <h3 className="text-xl font-semibold mb-4 text-purple-700">Productos Registrados</h3>
            {products.length === 0 ? (
                <p className="text-gray-600">No hay productos registrados aún. ¡Agrega uno!</p>
            ) : (
                <div className="overflow-x-auto rounded-lg shadow-md border border-gray-200">
                    <table className="min-w-full divide-y divide-gray-200">
                        <thead className="bg-gray-50">
                            <tr>
                                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                    Producto
                                </th>
                                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                    Unidad
                                </th>
                                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                    Cantidad
                                </th>
                                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                    Costo Total
                                </th>
                                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                    Costo por Unidad
                                </th>
                                <th scope="col" className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                                    Acciones
                                </th>
                            </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-gray-200">
                            {products.map((product) => (
                                <tr key={product.id} className="hover:bg-gray-50">
                                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                                        {product.name}
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                        {product.unit}
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                        {product.quantity}
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                        ${product.originalCost.toFixed(2)}
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                        ${product.costPerUnit.toFixed(4)}
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                                        <button
                                            onClick={() => handleEditClick(product)}
                                            className="text-purple-600 hover:text-purple-900 mr-3 p-1 rounded-md hover:bg-purple-100 transition-colors"
                                            title="Editar"
                                        >
                                            <Edit />
                                        </button>
                                        <button
                                            onClick={() => handleDeleteProduct(product.id)}
                                            className="text-red-600 hover:text-red-900 p-1 rounded-md hover:bg-red-100 transition-colors"
                                            title="Eliminar"
                                        >
                                            <Trash2 />
                                        </button>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}
        </div>
    );
};

const DishCosting = ({ dishToEdit }) => {
    const { clearEditingDish } = useContext(AppContext);
    const [dishName, setDishName] = useState('');
    const [searchTerm, setSearchTerm] = useState('');
    const [selectedProductData, setSelectedProductData] = useState(null);
    const [quantityUsed, setQuantityUsed] = useState('');
    const [ingredients, setIngredients] = useState([]);
    const [products, setProducts] = useState([]); // Products from localStorage
    const [totalCost, setTotalCost] = useState(0);
    const [profitPercentage, setProfitPercentage] = useState(30);
    const [sellingPrice, setSellingPrice] = useState(0);
    const [calculationMode, setCalculationMode] = useState('profitPercentage');
    const [error, setError] = useState(null);
    const [isSaving, setIsSaving] = useState(false);
    const searchResultsRef = useRef(null);
    const [isEditing, setIsEditing] = useState(false); // New state to manage edit mode
    const [currentDishId, setCurrentDishId] = useState(null); // To store the ID of the dish being edited

    // Lucide React icons
    const Plus = () => <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="lucide lucide-plus"><path d="M12 5v14"/><path d="M5 12h14"/></svg>;
    const Save = () => <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="lucide lucide-save"><path d="M19 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11l5 5v11a2 2 0 0 1-2 2Z"/><polyline points="17 21 17 13 7 13 7 21"/><polyline points="7 3 7 8 15 8"/></svg>;
    const Trash2 = () => <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="lucide lucide-trash-2"><path d="M3 6h18"/><path d="M19 6v14c0 1-1 2-2 2H7c-1 0-2-1-2-2V6"/><path d="M8 6V4c0-1 1-2 2-2h4c1 0 2 1 2 2v2"/><line x1="10" x2="10" y1="11" y2="17"/><line x1="14" x2="14" y1="11" y2="17"/></svg>;
    const Utensils = () => <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="lucide lucide-utensils"><path d="M3 2v7c0 1.1.9 2 2 2h4a2 2 0 0 0 2-2V2"/><path d="M7 2v20"/><path d="M21 15V2c0-1.1-.9-2-2-2h-4a2 2 0 0 0-2 2v13"/><path d="M15 2v20"/></svg>;
    const Percent = () => <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="lucide lucide-percent"><line x1="19" x2="5" y1="5" y2="19"/><circle cx="6.5" cy="6.5" r="2.5"/><circle cx="17.5" cy="17.5" r="2.5"/></svg>;
    const DollarSign = () => <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="lucide lucide-dollar-sign"><line x1="12" x2="12" y1="2" y2="22"/><path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6"/></svg>;
    const XCircle = () => <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="lucide lucide-x-circle"><circle cx="12" cy="12" r="10"/><path d="m15 9-6 6"/><path d="m9 9 6 6"/></svg>;

    // Load products from localStorage
    useEffect(() => {
        try {
            const storedProducts = localStorage.getItem('restaurant_products');
            if (storedProducts) {
                setProducts(JSON.parse(storedProducts));
            }
        } catch (e) {
            console.error("Error loading products for costing from localStorage:", e);
        }
    }, []);

    // Effect to handle dish editing: populate form when dishToEdit changes
    useEffect(() => {
        if (dishToEdit) {
            setDishName(dishToEdit.name || '');
            setIngredients(dishToEdit.ingredients || []);
            setTotalCost(dishToEdit.totalCost || 0);
            setProfitPercentage(dishToEdit.profitPercentage || 30);
            setSellingPrice(dishToEdit.sellingPrice || 0);
            // Determine calculationMode based on which value is more "set" or preferred for editing
            // For simplicity, we'll assume it was saved with profitPercentage as primary
            setCalculationMode('profitPercentage');
            setIsEditing(true);
            setCurrentDishId(dishToEdit.id);
            setError(null);
        } else {
            // Reset form if no dish is being edited
            setDishName('');
            setIngredients([]);
            setTotalCost(0);
            setProfitPercentage(30);
            setSellingPrice(0);
            setCalculationMode('profitPercentage');
            setIsEditing(false);
            setCurrentDishId(null);
            setError(null);
        }
    }, [dishToEdit]);


    useEffect(() => {
        // Recalculate total cost whenever ingredients change
        const cost = ingredients.reduce((sum, item) => sum + item.costContribution, 0);
        setTotalCost(cost);
    }, [ingredients]);

    useEffect(() => {
        // Recalculate based on the current calculation mode
        if (calculationMode === 'profitPercentage') {
            const profitFactor = 1 + (profitPercentage / 100);
            setSellingPrice(totalCost * profitFactor);
        } else { // calculationMode === 'sellingPrice'
            if (totalCost > 0) {
                const calculatedProfit = ((sellingPrice / totalCost) - 1) * 100;
                setProfitPercentage(calculatedProfit);
            } else {
                setProfitPercentage(0); // If cost is 0, profit is 0
            }
        }
    }, [totalCost, profitPercentage, sellingPrice, calculationMode]);

    // Filtered products for search
    const filteredProducts = products.filter(p =>
        p.name.toLowerCase().includes(searchTerm.toLowerCase())
    );

    const handleAddIngredient = () => {
        if (!selectedProductData || !quantityUsed) {
            setError("Por favor, selecciona un producto y la cantidad a usar.");
            return;
        }

        const product = selectedProductData; // Use the stored selected product data
        const qtyNum = parseFloat(quantityUsed);

        if (!product) {
            setError("Producto no encontrado.");
            return;
        }
        if (isNaN(qtyNum) || qtyNum <= 0) {
            setError("La cantidad a usar debe ser un número positivo.");
            return;
        }

        const costContribution = qtyNum * product.costPerUnit;

        setIngredients(prev => {
            // Check if ingredient already exists to update quantity instead of adding new entry
            const existingIngredientIndex = prev.findIndex(item => item.productId === product.id);
            if (existingIngredientIndex > -1) {
                const updatedIngredients = [...prev];
                const existingItem = updatedIngredients[existingIngredientIndex];
                existingItem.quantityUsed += qtyNum;
                existingItem.costContribution += costContribution;
                return updatedIngredients;
            } else {
                return [
                    ...prev,
                    {
                        productId: product.id,
                        name: product.name,
                        unit: product.unit,
                        quantityUsed: qtyNum,
                        costPerUnit: product.costPerUnit,
                        costContribution: costContribution,
                    }
                ];
            }
        });

        setSelectedProductData(null); // Clear selected product
        setSearchTerm(''); // Clear search term
        setQuantityUsed('');
        setError(null);
    };

    const handleRemoveIngredient = (indexToRemove) => {
        setIngredients(prev => prev.filter((_, index) => index !== indexToRemove));
    };

    const handleSaveOrUpdateDish = () => {
        if (!dishName.trim()) {
            setError("Por favor, ingresa un nombre para el platillo.");
            return;
        }
        if (ingredients.length === 0) {
            setError("Por favor, agrega al menos un ingrediente al platillo.");
            return;
        }

        setIsSaving(true);
        try {
            const storedDishes = JSON.parse(localStorage.getItem('restaurant_dishes') || '[]');
            let updatedDishes;
            const dishData = {
                name: dishName,
                ingredients: ingredients,
                totalCost: totalCost,
                profitPercentage: profitPercentage,
                sellingPrice: sellingPrice,
            };

            if (isEditing && currentDishId) {
                updatedDishes = storedDishes.map(dish =>
                    dish.id === currentDishId
                        ? { ...dish, ...dishData, updatedAt: new Date().toISOString() }
                        : dish
                );
                alert("¡Platillo actualizado exitosamente!");
            } else {
                const newDish = {
                    id: generateUniqueId(),
                    ...dishData,
                    createdAt: new Date().toISOString(),
                };
                updatedDishes = [...storedDishes, newDish];
                alert("¡Platillo guardado exitosamente!");
            }

            localStorage.setItem('restaurant_dishes', JSON.stringify(updatedDishes));

            // Clear form and editing state
            setDishName('');
            setIngredients([]);
            setTotalCost(0);
            setProfitPercentage(30);
            setSellingPrice(0);
            setCalculationMode('profitPercentage');
            setIsEditing(false);
            setCurrentDishId(null);
            clearEditingDish(); // Clear the editing dish in App component
            setError(null);

        } catch (e) {
            console.error("Error al guardar/actualizar platillo:", e);
            setError("Error al guardar/actualizar el platillo. Intente de nuevo.");
        } finally {
            setIsSaving(false);
        }
    };

    const handleProductSelect = (product) => {
        setSelectedProductData(product);
        setSearchTerm(product.name); // Display selected product name in search input
    };

    // Close search results when clicking outside
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (searchResultsRef.current && !searchResultsRef.current.contains(event.target)) {
                if (selectedProductData && searchTerm !== selectedProductData.name) {
                    setSearchTerm(selectedProductData.name);
                } else if (!selectedProductData) {
                    setSearchTerm('');
                }
            }
        };
        document.addEventListener("mousedown", handleClickOutside);
        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, [searchTerm, selectedProductData]);


    return (
        <div className="bg-white p-6 rounded-xl shadow-lg">
            <h2 className="text-2xl font-semibold mb-6 text-purple-700">{isEditing ? 'Editar Platillo' : 'Costeo de Platillos'}</h2>

            <div className="mb-6">
                <label htmlFor="dishName" className="text-sm font-medium text-gray-700 mb-1 block">Nombre del Platillo:</label>
                <input
                    type="text"
                    id="dishName"
                    value={dishName}
                    onChange={(e) => setDishName(e.target.value)}
                    className="p-2 border border-gray-300 rounded-lg w-full focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                    placeholder="Ej. Sopa de Tortilla"
                    required
                />
            </div>

            <h3 className="text-xl font-semibold mb-4 text-purple-700 flex items-center">
                <Utensils className="mr-2" /> Ingredientes
            </h3>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6 items-end">
                <div className="flex flex-col relative">
                    <label htmlFor="productSearch" className="text-sm font-medium text-gray-700 mb-1">Buscar Producto:</label>
                    <input
                        type="text"
                        id="productSearch"
                        value={searchTerm}
                        onChange={(e) => {
                            setSearchTerm(e.target.value);
                            setSelectedProductData(null);
                        }}
                        className="p-2 border border-gray-300 rounded-lg w-full focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                        placeholder="Escribe para buscar..."
                    />
                    {searchTerm && !selectedProductData && filteredProducts.length > 0 && (
                        <ul ref={searchResultsRef} className="absolute z-10 w-full bg-white border border-gray-300 rounded-lg mt-1 max-h-60 overflow-y-auto shadow-lg">
                            {filteredProducts.map(p => (
                                <li
                                    key={p.id}
                                    onClick={() => handleProductSelect(p)}
                                    className="p-2 hover:bg-purple-100 cursor-pointer border-b border-gray-100 last:border-b-0"
                                >
                                    {p.name} ({p.unit}) - ${p.costPerUnit.toFixed(4)}/{p.unit}
                                </li>
                            ))}
                        </ul>
                    )}
                    {searchTerm && !selectedProductData && filteredProducts.length === 0 && (
                        <div className="absolute z-10 w-full bg-white border border-gray-300 rounded-lg mt-1 p-2 text-gray-500 shadow-lg">
                            No se encontraron productos.
                        </div>
                    )}
                    {selectedProductData && (
                        <p className="text-sm text-gray-600 mt-1">
                            Producto seleccionado: <span className="font-semibold">{selectedProductData.name}</span>
                        </p>
                    )}
                </div>
                <div className="flex flex-col">
                    <label htmlFor="quantityUsed" className="text-sm font-medium text-gray-700 mb-1">Cantidad a Usar:</label>
                    <input
                        type="number"
                        id="quantityUsed"
                        value={quantityUsed}
                        onChange={(e) => setQuantityUsed(e.target.value)}
                        className="p-2 border border-gray-300 rounded-lg w-full focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                        placeholder="Ej. 0.5"
                        step="0.001"
                        disabled={!selectedProductData}
                    />
                </div>
                <div>
                    <button
                        onClick={handleAddIngredient}
                        className="bg-green-600 hover:bg-green-700 text-white font-bold py-2 px-4 rounded-lg w-full flex items-center justify-center transition-colors duration-300 shadow-md"
                        disabled={!selectedProductData || !quantityUsed}
                    >
                        <Plus className="mr-2" /> Agregar Ingrediente
                    </button>
                </div>
            </div>

            {error && (
                <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded-lg relative mb-4" role="alert">
                    <span className="block sm:inline">{error}</span>
                </div>
            )}

            {ingredients.length === 0 ? (
                <p className="text-gray-600 mb-6">Agrega ingredientes para calcular el costo del platillo.</p>
            ) : (
                <div className="overflow-x-auto rounded-lg shadow-md border border-gray-200 mb-6">
                    <table className="min-w-full divide-y divide-gray-200">
                        <thead className="bg-gray-50">
                            <tr>
                                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                    Ingrediente
                                </th>
                                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                    Cantidad Usada
                                </th>
                                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                    Costo por Unidad
                                </th>
                                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                    Contribución al Costo
                                </th>
                                <th scope="col" className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                                    Acciones
                                </th>
                            </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-gray-200">
                            {ingredients.map((item, index) => (
                                <tr key={index} className="hover:bg-gray-50">
                                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                                        {item.name}
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                        {item.quantityUsed.toFixed(3)} {item.unit}
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                        ${item.costPerUnit.toFixed(4)}
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                        ${item.costContribution.toFixed(2)}
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                                        <button
                                            onClick={() => handleRemoveIngredient(index)}
                                            className="text-red-600 hover:text-red-900 p-1 rounded-md hover:bg-red-100 transition-colors"
                                            title="Eliminar Ingrediente"
                                        >
                                            <Trash2 />
                                        </button>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}

            <div className="bg-purple-50 p-4 rounded-lg shadow-inner mb-6">
                <h3 className="text-xl font-semibold text-purple-800 mb-3">Resumen del Costo</h3>
                <p className="text-lg text-gray-800 flex items-center">
                    <DollarSign className="mr-2" /> Costo Total del Platillo: <span className="font-bold text-purple-700 ml-2 text-2xl">${totalCost.toFixed(2)}</span>
                </p>
            </div>

            <div className="bg-purple-50 p-4 rounded-lg shadow-inner mb-6">
                <h3 className="text-xl font-semibold text-purple-800 mb-3">Cálculo de Precio de Venta</h3>

                <div className="flex items-center mb-4 space-x-4">
                    <label className="flex items-center cursor-pointer">
                        <input
                            type="radio"
                            name="calculationMode"
                            value="profitPercentage"
                            checked={calculationMode === 'profitPercentage'}
                            onChange={() => setCalculationMode('profitPercentage')}
                            className="form-radio h-4 w-4 text-purple-600"
                        />
                        <span className="ml-2 text-gray-700">Calcular Precio de Venta (con % de Ganancia)</span>
                    </label>
                    <label className="flex items-center cursor-pointer">
                        <input
                            type="radio"
                            name="calculationMode"
                            value="sellingPrice"
                            checked={calculationMode === 'sellingPrice'}
                            onChange={() => setCalculationMode('sellingPrice')}
                            className="form-radio h-4 w-4 text-purple-600"
                        />
                        <span className="ml-2 text-gray-700">Calcular % de Ganancia (con Precio de Venta)</span>
                    </label>
                </div>

                {calculationMode === 'profitPercentage' ? (
                    <div className="flex items-center mb-4">
                        <label htmlFor="profitPercentage" className="text-sm font-medium text-gray-700 mr-2 flex items-center">
                            <Percent className="mr-1" /> Porcentaje de Ganancia Deseado:
                        </label>
                        <input
                            type="number"
                            id="profitPercentage"
                            value={profitPercentage}
                            onChange={(e) => setProfitPercentage(parseFloat(e.target.value))}
                            className="p-2 border border-gray-300 rounded-lg w-24 focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                            step="0.1"
                            min="0"
                        />
                        <span className="ml-2 text-lg font-bold text-purple-700">%</span>
                    </div>
                ) : (
                    <div className="flex items-center mb-4">
                        <label htmlFor="sellingPriceInput" className="text-sm font-medium text-gray-700 mr-2 flex items-center">
                            <DollarSign className="mr-1" /> Precio de Venta Deseado:
                        </label>
                        <input
                            type="number"
                            id="sellingPriceInput"
                            value={sellingPrice}
                            onChange={(e) => setSellingPrice(parseFloat(e.target.value))}
                            className="p-2 border border-gray-300 rounded-lg w-32 focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                            step="0.01"
                            min="0"
                        />
                        <span className="ml-2 text-lg font-bold text-purple-700">$</span>
                    </div>
                )}

                {calculationMode === 'profitPercentage' ? (
                    <p className="text-lg text-gray-800 flex items-center">
                        <DollarSign className="mr-2" /> Precio de Venta Sugerido: <span className="font-bold text-purple-700 ml-2 text-2xl">${sellingPrice.toFixed(2)}</span>
                    </p>
                ) : (
                    <p className="text-lg text-gray-800 flex items-center">
                        <Percent className="mr-2" /> Porcentaje de Ganancia Calculado: <span className="font-bold text-purple-700 ml-2 text-2xl">{profitPercentage.toFixed(2)}%</span>
                    </p>
                )}
            </div>

            <div className="flex space-x-2 justify-center">
                <button
                    onClick={handleSaveOrUpdateDish}
                    className="bg-purple-600 hover:bg-purple-700 text-white font-bold py-3 px-6 rounded-lg flex items-center justify-center transition-colors duration-300 shadow-lg text-lg"
                    disabled={isSaving}
                >
                    {isSaving ? (
                        <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                        </svg>
                    ) : (
                        <Save className="mr-3" />
                    )}
                    {isSaving ? 'Guardando...' : (isEditing ? 'Actualizar Platillo' : 'Guardar Platillo')}
                </button>
                {isEditing && (
                    <button
                        onClick={clearEditingDish}
                        className="bg-gray-500 hover:bg-gray-600 text-white font-bold py-3 px-6 rounded-lg flex items-center justify-center transition-colors duration-300 shadow-lg text-lg"
                    >
                        <XCircle className="mr-3" /> Cancelar Edición
                    </button>
                )}
            </div>
        </div>
    );
};

const DishList = () => {
    const { startEditingDish } = useContext(AppContext);
    const [dishes, setDishes] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [selectedDish, setSelectedDish] = useState(null);
    const [showModal, setShowModal] = useState(false);
    const [products, setProducts] = useState([]); // To map product IDs to names for import validation

    // Lucide React icons
    const Eye = () => <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="lucide lucide-eye"><path d="M2 12s3-7 10-7 10 7 10 7-3 7-10 7-10-7-10-7Z"/><circle cx="12" cy="12" r="3"/></svg>;
    const Trash2 = () => <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="lucide lucide-trash-2"><path d="M3 6h18"/><path d="M19 6v14c0 1-1 2-2 2H7c-1 0-2-1-2-2V6"/><path d="M8 6V4c0-1 1-2 2-2h4c1 0 2 1 2 2v2"/><line x1="10" x2="10" y1="11" y2="17"/><line x1="14" x2="14" y1="11" y2="17"/></svg>;
    const X = () => <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="lucide lucide-x"><path d="M18 6 6 18"/><path d="m6 6 12 12"/></svg>;
    const Download = () => <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="lucide lucide-download"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" x2="12" y1="15" y2="3"/></svg>;
    const Edit = () => <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="lucide lucide-edit"><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/><path d="M18.5 2.5a2.12 2.12 0 0 1 3 3L12 15l-4 1 1-4Z"/></svg>;
    const Upload = () => <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="lucide lucide-upload"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="17 8 12 3 7 8"/><line x1="12" x2="12" y1="3" y2="15"/></svg>;


    // Load dishes from localStorage
    useEffect(() => {
        try {
            const storedDishes = localStorage.getItem('restaurant_dishes');
            if (storedDishes) {
                setDishes(JSON.parse(storedDishes));
            }
        } catch (e) {
            console.error("Error loading dishes from localStorage:", e);
            setError("Error al cargar platillos desde el almacenamiento local.");
        } finally {
            setLoading(false);
        }
    }, []);

    // Load products to help with dish import validation
    useEffect(() => {
        try {
            const storedProducts = localStorage.getItem('restaurant_products');
            if (storedProducts) {
                setProducts(JSON.parse(storedProducts));
            }
        } catch (e) {
            console.error("Error loading products for dish import validation:", e);
        }
    }, []);

    const handleDeleteDish = (id) => {
        if (window.confirm("¿Estás seguro de que quieres eliminar este platillo?")) {
            try {
                const storedDishes = JSON.parse(localStorage.getItem('restaurant_dishes') || '[]');
                const updatedDishes = storedDishes.filter(dish => dish.id !== id);
                localStorage.setItem('restaurant_dishes', JSON.stringify(updatedDishes));
                setDishes(updatedDishes); // Update state to reflect deletion
                console.log("Platillo eliminado:", id);
            } catch (e) {
                console.error("Error al eliminar platillo:", e);
                setError("Error al eliminar el platillo. Intente de nuevo.");
            }
        }
    };

    const handleViewDetails = (dish) => {
        setSelectedDish(dish);
        setShowModal(true);
    };

    const closeModal = () => {
        setShowModal(false);
        setSelectedDish(null);
    };

    const exportDishesToCsv = () => {
        if (dishes.length === 0) {
            alert("No hay platillos para exportar.");
            return;
        }

        const csvRows = [];
        // Headers for dish summary, then dynamic headers for ingredients
        const baseHeaders = ["ID Platillo", "Nombre Platillo", "Costo Total", "Porcentaje Ganancia", "Precio Venta", "Fecha Creacion", "Ingredientes (JSON)"];
        csvRows.push(baseHeaders.map(escapeCsvValue).join(','));

        dishes.forEach(dish => {
            const dishData = [
                dish.id,
                dish.name,
                dish.totalCost.toFixed(2),
                dish.profitPercentage.toFixed(2),
                dish.sellingPrice.toFixed(2),
                new Date(dish.createdAt).toLocaleDateString('es-ES'), // Format date
            ];
            // Add ingredients as a JSON string in one column
            dishData.push(escapeCsvValue(JSON.stringify(dish.ingredients)));
            csvRows.push(dishData.map(escapeCsvValue).join(','));
        });

        const csvString = csvRows.join('\n');
        const blob = new Blob([csvString], { type: 'text/csv;charset=utf-8;' });
        if (typeof window.saveAs === 'function') {
            window.saveAs(blob, 'platillos.csv');
        } else {
            const link = document.createElement('a');
            link.href = URL.createObjectURL(blob);
            link.setAttribute('download', 'platillos.csv');
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
        }
    };

    const importDishesFromCsv = (event) => {
        const file = event.target.files[0];
        if (!file) {
            return;
        }

        const reader = new FileReader();
        reader.onload = (e) => {
            const text = e.target.result;
            const lines = text.split('\n').filter(line => line.trim() !== '');

            if (lines.length === 0) {
                setError("El archivo CSV está vacío.");
                return;
            }

            const headers = lines[0].split(',').map(h => h.trim());
            const expectedHeaders = ["ID Platillo", "Nombre Platillo", "Costo Total", "Porcentaje Ganancia", "Precio Venta", "Fecha Creacion", "Ingredientes (JSON)"];

            const missingHeaders = expectedHeaders.filter(header => !headers.includes(header));
            if (missingHeaders.length > 0) {
                setError(`Encabezados faltantes en el CSV: ${missingHeaders.join(', ')}. Asegúrate de que el archivo tenga los encabezados: ${expectedHeaders.join(', ')}`);
                return;
            }

            const newDishes = [];
            let importErrors = [];

            const productIdMap = new Map(products.map(p => [p.id, p])); // Map product IDs to product objects for quick lookup

            for (let i = 1; i < lines.length; i++) {
                const values = lines[i].split(/,(?=(?:(?:[^"]*"){2})*[^"]*$)/); // Split by comma, ignore commas inside quotes
                const unquotedValues = values.map(v => v.startsWith('"') && v.endsWith('"') ? v.slice(1, -1).replace(/""/g, '"') : v.trim());

                if (unquotedValues.length !== headers.length) {
                    importErrors.push(`Fila ${i + 1} tiene un número incorrecto de columnas. Saltando.`);
                    continue;
                }

                const dishData = {};
                headers.forEach((header, index) => {
                    dishData[header] = unquotedValues[index];
                });

                const id = dishData["ID Platillo"] || generateUniqueId();
                const name = dishData["Nombre Platillo"];
                const totalCost = parseFloat(dishData["Costo Total"]);
                const profitPercentage = parseFloat(dishData["Porcentaje Ganancia"]);
                const sellingPrice = parseFloat(dishData["Precio Venta"]);
                const createdAt = dishData["Fecha Creacion"] ? new Date(dishData["Fecha Creacion"]).toISOString() : new Date().toISOString();

                let ingredients = [];
                try {
                    // Attempt to parse the JSON string for ingredients
                    const parsedIngredients = JSON.parse(dishData["Ingredientes (JSON)"]);
                    if (Array.isArray(parsedIngredients)) {
                        ingredients = parsedIngredients.map(ing => {
                            // Validate and link ingredients to existing products
                            const product = productIdMap.get(ing.productId);
                            if (product) {
                                return {
                                    productId: ing.productId,
                                    name: product.name, // Use name from current products for consistency
                                    unit: product.unit, // Use unit from current products
                                    quantityUsed: parseFloat(ing.quantityUsed),
                                    costPerUnit: product.costPerUnit, // Use current cost per unit from products
                                    costContribution: parseFloat(ing.quantityUsed) * product.costPerUnit,
                                };
                            } else {
                                importErrors.push(`Fila ${i + 1}: Ingrediente "${ing.name}" (ID: ${ing.productId}) no encontrado en tus productos. Saltando este ingrediente.`);
                                return null; // Skip invalid ingredient
                            }
                        }).filter(Boolean); // Remove nulls
                    } else {
                        importErrors.push(`Fila ${i + 1}: Formato de ingredientes JSON inválido. Saltando.`);
                    }
                } catch (jsonError) {
                    importErrors.push(`Fila ${i + 1}: Error al parsear ingredientes JSON. Saltando.`);
                }

                if (!name || isNaN(totalCost) || isNaN(profitPercentage) || isNaN(sellingPrice) || ingredients.length === 0) {
                    importErrors.push(`Fila ${i + 1} contiene datos de platillo inválidos o faltantes. Saltando.`);
                    continue;
                }

                newDishes.push({
                    id: id,
                    name: name,
                    ingredients: ingredients,
                    totalCost: totalCost,
                    profitPercentage: profitPercentage,
                    sellingPrice: sellingPrice,
                    createdAt: createdAt,
                });
            }

            if (importErrors.length > 0) {
                setError(`Errores durante la importación de platillos:\n${importErrors.join('\n')}`);
            } else {
                setError(null);
            }

            // Merge new dishes with existing ones, updating if ID matches
            const updatedDishesMap = new Map(dishes.map(d => [d.id, d]));
            newDishes.forEach(newD => updatedDishesMap.set(newD.id, newD));
            setDishes(Array.from(updatedDishesMap.values()));
            alert("Platillos importados exitosamente. Revisa la tabla.");
            event.target.value = ''; // Clear file input
        };
        reader.readAsText(file);
    };


    if (loading) {
        return <div className="text-center text-lg mt-8">Cargando platillos...</div>;
    }

    if (error) {
        return <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded-lg relative mb-4" role="alert">
            <span className="block sm:inline">{error}</span>
        </div>;
    }

    return (
        <div className="bg-white p-6 rounded-xl shadow-lg">
            <h2 className="text-2xl font-semibold mb-6 text-purple-700">Platillos Guardados</h2>

            <div className="flex justify-end space-x-3 mb-6">
                <button
                    onClick={exportDishesToCsv}
                    className="bg-gray-700 hover:bg-gray-800 text-white font-bold py-2 px-4 rounded-lg flex items-center transition-colors duration-300 shadow-md"
                >
                    <Download className="mr-2" /> Exportar Platillos (.csv)
                </button>
                <label htmlFor="importDishesInput" className="bg-blue-600 hover:bg-blue-700 text-white font-bold py-2 px-4 rounded-lg flex items-center cursor-pointer transition-colors duration-300 shadow-md">
                    <Upload className="mr-2" /> Importar Platillos (.csv)
                    <input
                        id="importDishesInput"
                        type="file"
                        accept=".csv"
                        onChange={importDishesFromCsv}
                        className="hidden"
                    />
                </label>
            </div>

            {error && (
                <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded-lg relative mb-4" role="alert">
                    <span className="block sm:inline">{error}</span>
                </div>
            )}

            {dishes.length === 0 ? (
                <p className="text-gray-600">No hay platillos guardados aún. ¡Crea uno en la sección "Costeo de Platillos"!</p>
            ) : (
                <div className="overflow-x-auto rounded-lg shadow-md border border-gray-200">
                    <table className="min-w-full divide-y divide-gray-200">
                        <thead className="bg-gray-50">
                            <tr>
                                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                    Platillo
                                </th>
                                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                    Costo Total
                                </th>
                                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                    % Ganancia
                                </th>
                                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                    Precio de Venta
                                </th>
                                <th scope="col" className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                                    Acciones
                                </th>
                            </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-gray-200">
                            {dishes.map((dish) => (
                                <tr key={dish.id} className="hover:bg-gray-50">
                                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                                        {dish.name}
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                        ${dish.totalCost.toFixed(2)}
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                        {dish.profitPercentage.toFixed(2)}%
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                        ${dish.sellingPrice.toFixed(2)}
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                                        <button
                                            onClick={() => handleViewDetails(dish)}
                                            className="text-purple-600 hover:text-purple-900 mr-3 p-1 rounded-md hover:bg-purple-100 transition-colors"
                                            title="Ver Detalles"
                                        >
                                            <Eye />
                                        </button>
                                        <button
                                            onClick={() => startEditingDish(dish)}
                                            className="text-blue-600 hover:text-blue-900 mr-3 p-1 rounded-md hover:bg-blue-100 transition-colors"
                                            title="Editar Platillo"
                                        >
                                            <Edit />
                                        </button>
                                        <button
                                            onClick={() => handleDeleteDish(dish.id)}
                                            className="text-red-600 hover:text-red-900 p-1 rounded-md hover:bg-red-100 transition-colors"
                                            title="Eliminar"
                                        >
                                            <Trash2 />
                                        </button>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}

            {showModal && selectedDish && (
                <div className="fixed inset-0 bg-gray-600 bg-opacity-75 flex items-center justify-center p-4 z-50">
                    <div className="bg-white rounded-xl shadow-2xl p-6 w-full max-w-lg max-h-[90vh] overflow-y-auto relative">
                        <button
                            onClick={closeModal}
                            className="absolute top-4 right-4 text-gray-500 hover:text-gray-800 p-1 rounded-full hover:bg-gray-100 transition-colors"
                            title="Cerrar"
                        >
                            <X />
                        </button>
                        <h3 className="text-2xl font-bold text-purple-700 mb-4 border-b pb-2">Detalles del Platillo: {selectedDish.name}</h3>

                        <div className="mb-4">
                            <p className="text-lg text-gray-800 mb-1">
                                <span className="font-semibold">Costo Total:</span> ${selectedDish.totalCost.toFixed(2)}
                            </p>
                            <p className="text-lg text-gray-800 mb-1">
                                <span className="font-semibold">Porcentaje de Ganancia:</span> {selectedDish.profitPercentage.toFixed(2)}%
                            </p>
                            <p className="text-lg text-gray-800">
                                <span className="font-semibold">Precio de Venta Sugerido:</span> ${selectedDish.sellingPrice.toFixed(2)}
                            </p>
                        </div>

                        <h4 className="text-xl font-semibold text-purple-600 mb-3">Ingredientes:</h4>
                        {selectedDish.ingredients && selectedDish.ingredients.length > 0 ? (
                            <ul className="list-disc pl-5 space-y-2 text-gray-700">
                                {selectedDish.ingredients.map((ing, idx) => (
                                    <li key={idx}>
                                        <span className="font-medium">{ing.name}:</span> {ing.quantityUsed.toFixed(3)} {ing.unit} (Costo: ${ing.costContribution.toFixed(2)})
                                    </li>
                                ))}
                            </ul>
                        ) : (
                            <p className="text-gray-600">No hay ingredientes registrados para este platillo.</p>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
};

const InventoryManagement = () => {
    const [searchTerm, setSearchTerm] = useState('');
    const [selectedProductData, setSelectedProductData] = useState(null);
    const [quantityReceived, setQuantityReceived] = useState('');
    const [purchasePrice, setPurchasePrice] = useState('');
    const [entryDate, setEntryDate] = useState(new Date().toISOString().split('T')[0]); // YYYY-MM-DD
    const [inventoryEntries, setInventoryEntries] = useState([]);
    const [products, setProducts] = useState([]); // To fetch products for search
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [isEditing, setIsEditing] = useState(false);
    const [currentEntry, setCurrentEntry] = useState(null);
    const searchResultsRef = useRef(null);

    // Lucide React icons
    const PlusCircle = () => <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="lucide lucide-plus-circle"><circle cx="12" cy="12" r="10"/><path d="M8 12h8"/><path d="M12 8v8"/></svg>;
    const Edit = () => <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="lucide lucide-edit"><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/><path d="M18.5 2.5a2.12 2.12 0 0 1 3 3L12 15l-4 1 1-4Z"/></svg>;
    const Trash2 = () => <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="lucide lucide-trash-2"><path d="M3 6h18"/><path d="M19 6v14c0 1-1 2-2 2H7c-1 0-2-1-2-2V6"/><path d="M8 6V4c0-1 1-2 2-2h4c1 0 2 1 2 2v2"/><line x1="10" x2="10" y1="11" y2="17"/><line x1="14" x2="14" y1="11" y2="17"/></svg>;
    const Save = () => <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="lucide lucide-save"><path d="M19 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11l5 5v11a2 2 0 0 1-2 2Z"/><polyline points="17 21 17 13 7 13 7 21"/><polyline points="7 3 7 8 15 8"/></svg>;
    const XCircle = () => <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="lucide lucide-x-circle"><circle cx="12" cy="12" r="10"/><path d="m15 9-6 6"/><path d="m9 9 6 6"/></svg>;
    const Package = () => <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="lucide lucide-package"><path d="m7.5 4.27 9 5.15"/><path d="M2.5 8.67 12 14l9.5-5.33L12 3.33 2.5 8.67Z"/><path d="M7.5 19.73 12 22l4.5-2.27"/><path d="M12 14v8"/></svg>;


    // Load products from localStorage for the search dropdown
    useEffect(() => {
        try {
            const storedProducts = localStorage.getItem('restaurant_products');
            if (storedProducts) {
                setProducts(JSON.parse(storedProducts));
            }
        } catch (e) {
            console.error("Error loading products for inventory from localStorage:", e);
        }
    }, []);

    // Load inventory entries from localStorage
    useEffect(() => {
        try {
            const storedEntries = localStorage.getItem('restaurant_inventory_entries');
            if (storedEntries) {
                const entriesData = JSON.parse(storedEntries);
                entriesData.sort((a, b) => new Date(b.entryDate) - new Date(a.entryDate));
                setInventoryEntries(entriesData);
            }
        } catch (e) {
            console.error("Error loading inventory entries from localStorage:", e);
            setError("Error al cargar entradas de inventario desde el almacenamiento local.");
        } finally {
            setLoading(false);
        }
    }, []);

    // Save inventory entries to localStorage whenever inventoryEntries state changes
    useEffect(() => {
        if (!loading) {
            localStorage.setItem('restaurant_inventory_entries', JSON.stringify(inventoryEntries));
        }
    }, [inventoryEntries, loading]);

    const handleAddOrUpdateEntry = (e) => {
        e.preventDefault();
        if (!selectedProductData || !quantityReceived || !purchasePrice || !entryDate) {
            setError("Por favor, complete todos los campos.");
            return;
        }

        const qtyNum = parseFloat(quantityReceived);
        const priceNum = parseFloat(purchasePrice);

        if (isNaN(qtyNum) || isNaN(priceNum) || qtyNum <= 0 || priceNum <= 0) {
            setError("Cantidad recibida y precio de compra deben ser números positivos.");
            return;
        }

        const costPerUnitAtEntry = priceNum / qtyNum;

        try {
            let updatedEntries;
            if (isEditing) {
                updatedEntries = inventoryEntries.map(entry =>
                    entry.id === currentEntry.id
                        ? {
                            ...entry,
                            productId: selectedProductData.id,
                            productName: selectedProductData.name,
                            unit: selectedProductData.unit,
                            quantityReceived: qtyNum,
                            purchasePrice: priceNum,
                            costPerUnitAtEntry: costPerUnitAtEntry,
                            entryDate: entryDate,
                            updatedAt: new Date().toISOString(),
                        }
                        : entry
                );
                console.log("Entrada de inventario actualizada:", currentEntry.id);
            } else {
                const newEntry = {
                    id: generateUniqueId(),
                    productId: selectedProductData.id,
                    productName: selectedProductData.name,
                    unit: selectedProductData.unit,
                    quantityReceived: qtyNum,
                    purchasePrice: priceNum,
                    costPerUnitAtEntry: costPerUnitAtEntry,
                    entryDate: entryDate,
                    createdAt: new Date().toISOString(),
                };
                updatedEntries = [...inventoryEntries, newEntry];
                console.log("Entrada de inventario agregada:", selectedProductData.name);
            }

            // Sort and update state
            updatedEntries.sort((a, b) => new Date(b.entryDate) - new Date(a.entryDate));
            setInventoryEntries(updatedEntries);

            // Update the product's originalCost and costPerUnit in the products collection (localStorage)
            const storedProducts = JSON.parse(localStorage.getItem('restaurant_products') || '[]');
            const updatedProducts = storedProducts.map(p =>
                p.id === selectedProductData.id
                    ? { ...p, originalCost: priceNum, costPerUnit: costPerUnitAtEntry }
                    : p
            );
            localStorage.setItem('restaurant_products', JSON.stringify(updatedProducts));
            console.log(`Costo del producto "${selectedProductData.name}" actualizado en localStorage.`);


            // Clear form
            setSearchTerm('');
            setSelectedProductData(null);
            setQuantityReceived('');
            setPurchasePrice('');
            setEntryDate(new Date().toISOString().split('T')[0]);
            setIsEditing(false);
            setCurrentEntry(null);
            setError(null);
        } catch (e) {
            console.error("Error al guardar/actualizar entrada de inventario:", e);
            setError("Error al guardar/actualizar la entrada. Intente de nuevo.");
        }
    };

    const handleEditClick = (entry) => {
        setIsEditing(true);
        setCurrentEntry(entry);
        setSearchTerm(entry.productName);
        setSelectedProductData({ id: entry.productId, name: entry.productName, unit: entry.unit }); // Recreate product data for selection
        setQuantityReceived(entry.quantityReceived);
        setPurchasePrice(entry.purchasePrice);
        setEntryDate(entry.entryDate);
        setError(null);
    };

    const handleDeleteEntry = (id) => {
        if (window.confirm("¿Estás seguro de que quieres eliminar esta entrada de inventario?")) {
            try {
                const updatedEntries = inventoryEntries.filter(entry => entry.id !== id);
                setInventoryEntries(updatedEntries); // This will trigger localStorage update via useEffect
                console.log("Entrada de inventario eliminada:", id);
            } catch (e) {
                console.error("Error al eliminar entrada de inventario:", e);
                setError("Error al eliminar la entrada. Intente de nuevo.");
            }
        }
    };

    const handleCancelEdit = () => {
        setIsEditing(false);
        setCurrentEntry(null);
        setSearchTerm('');
        setSelectedProductData(null);
        setQuantityReceived('');
        setPurchasePrice('');
        setEntryDate(new Date().toISOString().split('T')[0]);
        setError(null);
    };

    const filteredProducts = products.filter(p =>
        p.name.toLowerCase().includes(searchTerm.toLowerCase())
    );

    const handleProductSelect = (product) => {
        setSelectedProductData(product);
        setSearchTerm(product.name);
        if (isEditing && currentEntry && currentEntry.productId !== product.id) {
            setQuantityReceived('');
            setPurchasePrice('');
        }
    };

    // Close search results when clicking outside
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (searchResultsRef.current && !searchResultsRef.current.contains(event.target)) {
                if (selectedProductData && searchTerm !== selectedProductData.name) {
                    setSearchTerm(selectedProductData.name);
                } else if (!selectedProductData) {
                    setSearchTerm('');
                }
            }
        };
        document.addEventListener("mousedown", handleClickOutside);
        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, [searchTerm, selectedProductData]);


    if (loading) {
        return <div className="text-center text-lg mt-8">Cargando inventario...</div>;
    }

    if (error) {
        return <div className="text-center text-red-600 text-lg mt-8">{error}</div>;
    }

    return (
        <div className="bg-white p-6 rounded-xl shadow-lg">
            <h2 className="text-2xl font-semibold mb-6 text-purple-700">Gestión de Inventario</h2>

            <form onSubmit={handleAddOrUpdateEntry} className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mb-8">
                <div className="flex flex-col relative">
                    <label htmlFor="productSearchInventory" className="text-sm font-medium text-gray-700 mb-1">Producto:</label>
                    <input
                        type="text"
                        id="productSearchInventory"
                        value={searchTerm}
                        onChange={(e) => {
                            setSearchTerm(e.target.value);
                            setSelectedProductData(null);
                        }}
                        className="p-2 border border-gray-300 rounded-lg w-full focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                        placeholder="Escribe para buscar un producto..."
                        required
                    />
                    {searchTerm && !selectedProductData && filteredProducts.length > 0 && (
                        <ul ref={searchResultsRef} className="absolute z-10 w-full bg-white border border-gray-300 rounded-lg mt-1 max-h-60 overflow-y-auto shadow-lg">
                            {filteredProducts.map(p => (
                                <li
                                    key={p.id}
                                    onClick={() => handleProductSelect(p)}
                                    className="p-2 hover:bg-purple-100 cursor-pointer border-b border-gray-100 last:border-b-0"
                                >
                                    {p.name} ({p.unit})
                                </li>
                            ))}
                        </ul>
                    )}
                    {searchTerm && !selectedProductData && filteredProducts.length === 0 && (
                        <div className="absolute z-10 w-full bg-white border border-gray-300 rounded-lg mt-1 p-2 text-gray-500 shadow-lg">
                            No se encontraron productos.
                        </div>
                    )}
                    {selectedProductData && (
                        <p className="text-sm text-gray-600 mt-1">
                            Producto seleccionado: <span className="font-semibold">{selectedProductData.name} ({selectedProductData.unit})</span>
                        </p>
                    )}
                </div>
                <div className="flex flex-col">
                    <label htmlFor="quantityReceived" className="text-sm font-medium text-gray-700 mb-1">Cantidad Recibida:</label>
                    <input
                        type="number"
                        id="quantityReceived"
                        value={quantityReceived}
                        onChange={(e) => setQuantityReceived(e.target.value)}
                        className="p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                        placeholder="Ej. 10"
                        step="0.01"
                        required
                    />
                </div>
                <div className="flex flex-col">
                    <label htmlFor="purchasePrice" className="text-sm font-medium text-gray-700 mb-1">Precio de Compra (Total):</label>
                    <input
                        type="number"
                        id="purchasePrice"
                        value={purchasePrice}
                        onChange={(e) => setPurchasePrice(e.target.value)}
                        className="p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                        placeholder="Ej. 50.75"
                        step="0.01"
                        required
                    />
                </div>
                <div className="flex flex-col">
                    <label htmlFor="entryDate" className="text-sm font-medium text-gray-700 mb-1">Fecha de Entrada:</label>
                    <input
                        type="date"
                        id="entryDate"
                        value={entryDate}
                        onChange={(e) => setEntryDate(e.target.value)}
                        className="p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                        required
                    />
                </div>
                <div className="md:col-span-3 flex justify-end space-x-2 mt-2">
                    {isEditing ? (
                        <>
                            <button
                                type="submit"
                                className="bg-green-600 hover:bg-green-700 text-white font-bold py-2 px-4 rounded-lg flex items-center transition-colors duration-300 shadow-md"
                            >
                                <Save className="mr-2" /> Guardar Cambios
                            </button>
                            <button
                                type="button"
                                onClick={handleCancelEdit}
                                className="bg-gray-500 hover:bg-gray-600 text-white font-bold py-2 px-4 rounded-lg flex items-center transition-colors duration-300 shadow-md"
                            >
                                <XCircle className="mr-2" /> Cancelar
                            </button>
                        </>
                    ) : (
                        <button
                            type="submit"
                            className="bg-purple-600 hover:bg-purple-700 text-white font-bold py-2 px-4 rounded-lg flex items-center transition-colors duration-300 shadow-md"
                        >
                            <PlusCircle className="mr-2" /> Registrar Entrada
                        </button>
                    )}
                </div>
            </form>

            {error && (
                <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded-lg relative mb-4" role="alert">
                    <span className="block sm:inline">{error}</span>
                </div>
            )}

            <h3 className="text-xl font-semibold mb-4 text-purple-700 flex items-center">
                <Package className="mr-2" /> Historial de Entradas de Inventario
            </h3>
            {inventoryEntries.length === 0 ? (
                <p className="text-gray-600">No hay entradas de inventario registradas aún.</p>
            ) : (
                <div className="overflow-x-auto rounded-lg shadow-md border border-gray-200">
                    <table className="min-w-full divide-y divide-gray-200">
                        <thead className="bg-gray-50">
                            <tr>
                                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                    Producto
                                </th>
                                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                    Cantidad Recibida
                                </th>
                                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                    Precio de Compra (Total)
                                </th>
                                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                    Costo por Unidad (en Entrada)
                                </th>
                                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                    Fecha de Entrada
                                </th>
                                <th scope="col" className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                                    Acciones
                                </th>
                            </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-gray-200">
                            {inventoryEntries.map((entry) => (
                                <tr key={entry.id} className="hover:bg-gray-50">
                                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                                        {entry.productName}
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                        {entry.quantityReceived.toFixed(2)} {entry.unit}
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                        ${entry.purchasePrice.toFixed(2)}
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                        ${entry.costPerUnitAtEntry.toFixed(4)}
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                        {entry.entryDate}
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                                        <button
                                            onClick={() => handleEditClick(entry)}
                                            className="text-purple-600 hover:text-purple-900 mr-3 p-1 rounded-md hover:bg-purple-100 transition-colors"
                                            title="Editar"
                                        >
                                            <Edit />
                                        </button>
                                        <button
                                            onClick={() => handleDeleteEntry(entry.id)}
                                            className="text-red-600 hover:text-red-900 p-1 rounded-md hover:bg-red-100 transition-colors"
                                            title="Eliminar"
                                        >
                                            <Trash2 />
                                        </button>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}
        </div>
    );
};

export default App;
